import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Clapperboard, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "monthly",
    label: "Monthly",
    price: "$24.99",
    cadence: "/ month",
    note: "Billed every 30 days. Cancel anytime.",
  },
  {
    id: "yearly",
    label: "Yearly",
    price: "$249",
    cadence: "/ year",
    perMonth: "$20.75/mo",
    monthlyEquivalent: "$299.88",
    savings: "$50.88",
    note: "Billed once a year. Cancel anytime.",
    badge: "2 months free",
  },
];

export default function BillingPage() {
  const { user, checkAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    axios.get(`${API}/billing/status`, { withCredentials: true })
      .then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const startCheckout = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`,
        { plan, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading("");
    }
  };

  const openPortal = async () => {
    setLoading("portal");
    try {
      const { data } = await axios.post(`${API}/billing/portal`,
        { return_url: window.location.href }, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
      setLoading("");
    }
  };

  const b = status;
  const trialing = b?.plan === "trial" && b?.is_active;
  const grandfathered = b?.plan === "grandfathered";
  const active = b?.plan === "active";
  const expired = b?.plan === "expired";

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="billing-page">
      <div className="flex items-center gap-3 mb-6">
        <Clapperboard className="text-amber-400" size={22} />
        <h1 className="text-3xl font-bold font-[Outfit]">Pricing &amp; Plans</h1>
      </div>

      <div className="rounded-xl border border-[#28303F] bg-[#14181F] p-5 mb-6" data-testid="billing-current-plan">
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Current plan</p>
        {!b ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : grandfathered ? (
          <p className="text-sm text-gray-200">You have <span className="text-amber-400 font-semibold">lifetime access</span> as a founding account. No subscription required.</p>
        ) : trialing ? (
          <p className="text-sm text-gray-200">
            You're on the <span className="text-amber-400 font-semibold">7-day free trial</span> —
            <span className="text-white font-semibold"> {b.days_left} day{b.days_left === 1 ? "" : "s"}</span> left.
            Pick a plan below to keep access after the trial ends.
          </p>
        ) : active ? (
          <p className="text-sm text-gray-200">
            Active subscription · renews {b.renewal_at ? new Date(b.renewal_at).toLocaleDateString() : "soon"}.
          </p>
        ) : expired ? (
          <p className="text-sm text-red-300">Your trial has ended. Choose a plan below to resume access.</p>
        ) : (
          <p className="text-sm text-gray-400 capitalize">{b.status}</p>
        )}
        {(active || expired) && (
          <button
            onClick={openPortal}
            disabled={loading === "portal"}
            data-testid="billing-portal-btn"
            className="mt-3 text-xs text-amber-300 hover:text-amber-200 underline underline-offset-2"
          >
            {loading === "portal" ? "Opening…" : "Manage billing · cancel · update card"}
          </button>
        )}
      </div>

      {!grandfathered && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PLANS.map((p) => (
            <div key={p.id} data-testid={`billing-plan-${p.id}`}
              className={`relative rounded-xl border bg-[#14181F] p-6 transition-colors ${
                p.id === "yearly"
                  ? "border-amber-500/60 ring-1 ring-amber-500/20 shadow-[0_0_40px_-20px_rgba(245,158,11,0.4)]"
                  : "border-[#28303F] hover:border-amber-500/60"
              }`}>
              {p.badge && (
                <span
                  data-testid={`billing-plan-${p.id}-badge`}
                  className="absolute -top-3 right-4 bg-amber-500 text-black text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg"
                >
                  {p.badge}
                </span>
              )}
              <h2 className="text-lg font-semibold font-[Outfit]">{p.label}</h2>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-white">{p.price}</span>
                <span className="text-sm text-gray-500">{p.cadence}</span>
              </div>
              {p.id === "yearly" ? (
                <div data-testid="billing-plan-yearly-savings" className="mt-2 space-y-0.5">
                  <p className="text-[11px] text-amber-300">
                    That's just <span className="font-semibold">{p.perMonth}</span>
                    <span className="text-gray-500"> · pay for 10, get 12</span>
                  </p>
                  <p className="text-[11px] text-gray-500">
                    Monthly would be <span className="line-through">{p.monthlyEquivalent}</span>
                    <span className="text-emerald-400 font-semibold"> — save {p.savings}/yr</span>
                  </p>
                </div>
              ) : (
                <p className="text-[11px] text-gray-500 mt-1">{p.note}</p>
              )}
              <ul className="mt-4 space-y-1.5 text-xs text-gray-300">
                {["Unlimited projects & call sheets", "Unlimited crew / talent / locations", "Email + SMS distribution", "Delivery tracking & RSVP", "PDF call-sheet generation"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check size={13} className="text-amber-400 shrink-0" />{f}</li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout(p.id)}
                disabled={loading === p.id}
                data-testid={`billing-checkout-${p.id}-btn`}
                className="mt-5 w-full bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/60 text-black font-semibold text-sm py-2.5 rounded-lg transition-colors"
              >
                {loading === p.id ? "Redirecting…" : active ? "Switch to this plan" : "Choose this plan"}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="mt-6 text-[11px] text-gray-600">
        Payments are processed securely by Stripe. We never see your card details.
        {user?.email && <> · Signed in as {user.email}.</>}
      </p>
    </div>
  );
}
